import type { ComponentProps } from 'react'
import React, { useState } from 'react'
import { Label } from '~/design-system/primitives'
import { cn } from '~/lib/cn'
import type { PaymentSystem } from '~/type/Cart'

type CardNumberInputProps = {
  onCreditCardNumberChange?: (number: string) => void
  creditCard?: PaymentSystem
} & ComponentProps<'input'>

const CARD_LENGTHS = {
  AMEX: 15,
  DEFAULT: 16,
} as const

const CardNumberInput: React.FC<CardNumberInputProps> = ({ onCreditCardNumberChange, ...props }) => {
  const [cardNumber, setCardNumber] = useState('')
  const [isValid, setIsValid] = useState(true)

  const isAmex = (value: string): boolean => {
    const cleaned = removeNonDigits(value)
    return cleaned.startsWith('3') && cleaned.length <= CARD_LENGTHS.AMEX
  }

  const removeNonDigits = (value: string): string => value.replace(/\D/g, '')

  const formatCardNumber = (value: string): string => {
    const cleaned = removeNonDigits(value)
    return isAmex(cleaned)
      ? cleaned.replace(/(\d{4})(\d{6})(\d{5})/g, '$1 $2 $3')
      : cleaned.replace(/(\d{4})/g, '$1 ').trim()
  }

  const getMaxLength = (value: string): number => (isAmex(value) ? CARD_LENGTHS.AMEX : CARD_LENGTHS.DEFAULT)

  const processCardNumber = (input: string): void => {
    const cleaned = removeNonDigits(input)
    const maxLength = getMaxLength(cleaned)
    const truncated = cleaned.slice(0, maxLength)
    const formatted = formatCardNumber(truncated)

    setCardNumber(formatted)
    setIsValid(cleaned.length === maxLength)
    onCreditCardNumberChange?.(cleaned)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    processCardNumber(event.target.value)
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>): void => {
    event.preventDefault()
    processCardNumber(event.clipboardData.getData('text'))
  }

  const getValidationMessage = (): string => {
    const requiredDigits = isAmex(cardNumber) ? CARD_LENGTHS.AMEX : CARD_LENGTHS.DEFAULT
    return `Digite ${requiredDigits} dígitos do cartão.`
  }

  return (
    <>
      <Label htmlFor="number">Número do cartão *</Label>

      <input
        className={cn(
          'w-full px-2 py-1.5 min-w-0 outline bg-white text-sm text-gray-800',
          'disabled:text-primary-black max-h-9 border rounded border-gray-60 outline-0',
          !isValid && 'border-red-700',
        )}
        id="credcardnumberInput"
        inputMode="numeric"
        maxLength={19}
        name="number"
        type="tel"
        value={cardNumber}
        onChange={handleChange}
        onPaste={handlePaste}
        {...props}
      />

      <p className={cn('text-sm', !isValid ? 'text-red-700' : 'text-gray-80')}>{getValidationMessage()}</p>
    </>
  )
}

export default CardNumberInput
